import { Stack, Text } from '@ltvco/refresh-lib/theme';
import { EnterPreferencesInstruction } from '../EnterPreferencesInstruction/EnterPreferencesInstruction';
import { VehicleMatchmakerResult } from '../VehicleMatchmakerResult/VehicleMatchmakerResult';
import { RetrySearchInstruction } from '../../components/RetrySearchInstruction';
import type { VehicleMatch } from '@ltvco/refresh-lib/api';
import { usePermissions } from '@ltvco/refresh-lib/ctx';
import { FreePlanDisclaimer } from 'components/ChatAI/components/FreePlanDisclaimer';
import { NO_MATCHES_MESSAGE } from '../constants';

interface IVehicleMatchmakerContentProps {
  onSendMessage?: (message?: string) => void;
  firstMatchmakerData: VehicleMatch[] | null;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  hasError: boolean;
  onNewSearch: () => void;
  isLoading: boolean;
  onOpenVehicleReport?: (match: VehicleMatch) => Promise<void>;
  openReportMatchKey?: string | null;
}

/**
 * VehicleMatchmakerContent - Main content component that shows either:
 * 1. The initial preferences form
 * 2. The matchmaker results
 * 3. Error state with retry option
 *
 * @component
 */
export const VehicleMatchmakerContent = ({
  onSendMessage,
  firstMatchmakerData,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
  hasError,
  onNewSearch,
  isLoading,
  onOpenVehicleReport,
  openReportMatchKey,
}: IVehicleMatchmakerContentProps) => {
  const { isFreePlan } = usePermissions();
  const hasConversationStarted = firstMatchmakerData !== null || hasError;
  const showResultCard =
    firstMatchmakerData &&
    firstMatchmakerData.length > 0 &&
    !hasError &&
    !isLoading;
  const showNoMatchesOrError =
    hasConversationStarted &&
    !isLoading &&
    (hasError ||
      (firstMatchmakerData !== null && firstMatchmakerData.length === 0));

  return (
    <Stack width="100%" alignItems="center" spacing={3}>
      <EnterPreferencesInstruction
        onSendMessage={onSendMessage}
        hasConversationStarted={hasConversationStarted}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={onResetComplete}
        onProcessingStateChange={onProcessingStateChange}
        isLoading={isLoading}
      />

      {showResultCard && (
        <>
          <VehicleMatchmakerResult
            matches={firstMatchmakerData}
            onDetailsClick={onOpenVehicleReport}
            openReportMatchKey={openReportMatchKey}
          />
          {isFreePlan && <FreePlanDisclaimer entityType="vehicle_matchmaker" />}
          <RetrySearchInstruction onRetrySearch={onNewSearch} />
        </>
      )}

      {showNoMatchesOrError && (
        <Stack width="100%" spacing={2}>
          <Text variant="body1">{NO_MATCHES_MESSAGE}</Text>
          <RetrySearchInstruction onRetrySearch={onNewSearch} />
        </Stack>
      )}
    </Stack>
  );
};
